import React, { useCallback, useState, useMemo, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { v4 as uuidV4 } from 'uuid'
import Search from '@groovehq/internal-design-system/lib/components/Search/Search'
import { divider } from '@groovehq/internal-design-system/lib/components/Dropdown/Dropdown'
import {
  doFetchCannedReplies,
  doFetchCannedReplyCategories,
  doFetchCannedReplyCategoriesAndTemplatesBySearch,
} from 'ducks/cannedReplies/operations'
import {
  selectLastRequestedSearchTerm,
  selectIsLoadingCannedReplyCategories,
  selectHasLoadedTemplates,
  selectMailboxIdForTemplate,
} from 'ducks/cannedReplies/selectors'
import { selectMailboxIdFromTicketOrCurrent } from 'selectors/mailboxes'
import { debounce } from 'util/functions'
import usePrevious from 'util/hooks/usePrevious'
import { ENTER, TAB } from 'constants/keys'
import KeyboardNavigator from 'components/KeyboardNavigator'
import { Loader } from 'shared/ui'
import { selectMailboxIds } from 'selectors/mailboxes_base'

import { styles } from './styles'
import SearchEmpty from '../SearchEmpty'
import Empty from '../Empty'
import DropdownFooter from '../DropdownFooter'
import RepliesInCategory from './RepliesInCategory'
import useHoverMenuItem from './useHoverMenuItem'

const KEYBOARD_NAVIGATOR_KEYS = { select: [ENTER, TAB] }
const recentlyUsedId = uuidV4()

const DropdownMenu = ({
  items,
  onSelect,
  recentlyUsedTemplates,
  hasLoadedReplyCategories,
  visible,
  onVisibleChange,
  setOpenPreview,
  onGetPreview,
  search,
  setSearch,
  setCurrentPreviewTemplateId,
  currentPreviewTemplateId,
  arrowPositionRef,
}) => {
  const searchRef = useRef()
  const containerRef = useRef()
  const menuRef = useRef()
  const searchTerm = useSelector(selectLastRequestedSearchTerm)
  const previousSearch = usePrevious(search)
  const dispatch = useDispatch()
  const [activeItemId, setActiveItemId] = useState()
  const isLoadingCategories = useSelector(selectIsLoadingCannedReplyCategories)
  const hasLoadedTemplates = useSelector(selectHasLoadedTemplates)

  const noCannedReplies =
    !items.length &&
    hasLoadedReplyCategories &&
    searchTerm === '' &&
    !isLoadingCategories

  const mailboxId = useSelector(selectMailboxIdFromTicketOrCurrent)
  const allMailboxesIds = useSelector(selectMailboxIds)
  const previousMailboxId = useSelector(selectMailboxIdForTemplate)

  const mailboxIds = useMemo(
    () => {
      return mailboxId ? [mailboxId] : allMailboxesIds
    },
    [mailboxId, allMailboxesIds]
  )

  const debouncedSearch = useMemo(
    () =>
      debounce(query => {
        dispatch(
          doFetchCannedReplyCategoriesAndTemplatesBySearch(query, mailboxIds)
        )
      }, 500),
    [dispatch, mailboxIds]
  )
  const debouncedGetPreview = useMemo(() => debounce(onGetPreview, 200), [
    onGetPreview,
  ])

  const list = useMemo(
    () => {
      if (hasLoadedReplyCategories && !hasLoadedTemplates) return []
      if (!items.length) return []
      if (!recentlyUsedTemplates.length) return items
      const itemsCopy = [...items]
      itemsCopy.unshift({
        id: recentlyUsedId,
        name: 'Recently used',
        total: recentlyUsedTemplates.length,
        type: 'category',
        templates: recentlyUsedTemplates,
      })

      return itemsCopy
    },
    [items, recentlyUsedTemplates, hasLoadedReplyCategories, hasLoadedTemplates]
  )

  const listWithoutEmptyTemplates = useMemo(
    () => {
      return list.filter(item => {
        if (item.type === 'category') {
          return item.templates.filter(template => template.id > 0).length
        }
        return true
      })
    },
    [list]
  )

  const noSearchResults =
    !!searchTerm.trim() &&
    !listWithoutEmptyTemplates.length &&
    hasLoadedReplyCategories

  const handleHeaderClick = useCallback(
    item => {
      if (item.type === 'category' && item.name !== 'Recently used') {
        if (!item.loaded && !item.loading) {
          dispatch(doFetchCannedReplies([item.id], searchTerm, mailboxIds))
        }
      }
      searchRef.current.focus()
    },
    [dispatch, searchTerm, mailboxIds]
  )

  const handleShowPreview = useCallback(
    (template, target) => {
      // eslint-disable-next-line no-param-reassign
      arrowPositionRef.current =
        target?.getBoundingClientRect().top -
        containerRef.current?.getBoundingClientRect().top
      setOpenPreview(true)
      setCurrentPreviewTemplateId(template?.id)
      if (currentPreviewTemplateId !== template?.id) {
        debouncedGetPreview(template)
      }
    },
    [
      debouncedGetPreview,
      setOpenPreview,
      currentPreviewTemplateId,
      setCurrentPreviewTemplateId,
      arrowPositionRef,
    ]
  )

  const handleChangeSearch = useCallback(
    e => {
      const { value } = e.target
      setOpenPreview(false)
      setSearch(value)
      debouncedSearch(value)
    },
    [debouncedSearch, setSearch, setOpenPreview]
  )

  useEffect(
    () => {
      const hasData = !searchTerm?.trim() && items.length

      if (!visible) return
      if (search.trim()) return

      if (
        (previousSearch?.trim() && !search.trim()) ||
        (!hasData || !hasLoadedTemplates) ||
        previousMailboxId !== mailboxId
      ) {
        dispatch(doFetchCannedReplyCategories())
        dispatch(doFetchCannedReplies([], '', mailboxIds))
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visible, dispatch, search]
  )

  useEffect(
    () => {
      if (recentlyUsedTemplates.length) {
        setActiveItemId(recentlyUsedId)
      }
    },
    [recentlyUsedTemplates.length]
  )

  useEffect(
    () => {
      if (visible && searchRef.current) {
        searchRef.current.focus()
      }
    },
    [visible]
  )

  const hasSearch = !!searchTerm.trim()

  const handleActivateCategory = useCallback(
    id => {
      setActiveItemId(id)
      setOpenPreview(false)
    },
    [setOpenPreview]
  )

  const handleActivateTemplate = useCallback(
    (templateWithCatId, template, _, target) => {
      setActiveItemId(templateWithCatId)
      handleShowPreview(template, target)
    },
    [handleShowPreview]
  )

  const handleActivateByMouse = useCallback(
    (item, id, e) => {
      if (item) {
        handleActivateTemplate(id, item, e, e.target)
      } else {
        handleActivateCategory(id, e, e.target)
      }
    },
    [handleActivateTemplate, handleActivateCategory]
  )

  const { handleMouseEnterItem, handleMouseLeaveItem } = useHoverMenuItem({
    menuElement: menuRef.current,
    onActivate: handleActivateByMouse,
    activeItemId,
    setActiveItemId,
  })

  if (noCannedReplies) {
    return <Empty css={styles.container} onVisibleChange={onVisibleChange} />
  }

  return (
    <div css={styles.container} ref={containerRef}>
      <div css={styles.searchContainer}>
        <Search
          placeholder="Search replies…"
          onChange={handleChangeSearch}
          value={search}
          ref={searchRef}
          css={styles.search}
        />
        {divider}
      </div>
      {isLoadingCategories && (
        <div className="grui p-12">
          <Loader />
        </div>
      )}
      {noSearchResults &&
        !isLoadingCategories && (
          <SearchEmpty onVisibleChange={onVisibleChange} />
        )}
      {!noSearchResults &&
        !isLoadingCategories && (
          <KeyboardNavigator
            activeKey={searchTerm}
            keys={KEYBOARD_NAVIGATOR_KEYS}
            focusElement={searchRef.current}
            css={styles.templatesWrapper}
            // We want to control it to allow more forgiving mouse leave
            activateOnHover={false}
          >
            <div className="grui pb-10" ref={menuRef}>
              {listWithoutEmptyTemplates.map(item => (
                <RepliesInCategory
                  key={item.id}
                  item={item}
                  handleHeaderClick={handleHeaderClick}
                  onSelect={onSelect}
                  active={recentlyUsedId === item.id || hasSearch}
                  handleMouseEnterItem={handleMouseEnterItem}
                  handleMouseLeaveItem={handleMouseLeaveItem}
                  activeItemId={activeItemId}
                  handleActivateCategory={handleActivateCategory}
                  handleActivateTemplate={handleActivateTemplate}
                />
              ))}
            </div>
          </KeyboardNavigator>
        )}
      {<DropdownFooter onVisibleChange={onVisibleChange} />}
    </div>
  )
}

export default React.memo(DropdownMenu)
